import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, PieChart, Pie, Cell,
} from 'recharts';
import { api, money, number, percent, date, dateTime, humanise, relative } from '@/lib/api';
import { chartPalette, chartChrome, fonts } from '@/tokens';
import { Delta, StatusBadge, EmptyState, SkeletonRows, Spinner, Select } from '@/components/ui/primitives';

export type WidgetSize = 'sm' | 'md' | 'lg';

export interface WidgetInstance {
  id: string;
  widget: string;
  size: WidgetSize;
  collapsed: boolean;
}

export interface WidgetDef {
  key: string;
  label: string;
  description: string;
  group: string;
  defaultSize: WidgetSize;
}

/**
 * Chart palette — indigo leads, orange is the counterpoint, then purple,
 * cyan and emerald for longer series.
 */
const SERIES_COLOURS = [...chartPalette];

const AXIS = { fill: chartChrome.axis, fontFamily: fonts.sans, fontSize: 10 };
const TOOLTIP_STYLE = {
  background: chartChrome.tooltipBg,
  color: chartChrome.tooltipText,
  border: 'none',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
  fontFamily: fonts.sans,
  fontSize: 11,
};

const RANGES = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'quarter', label: 'This quarter' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'year', label: 'This year' },
];

/** Widgets whose data is time-based and therefore worth a range selector. */
const RANGED = new Set([
  'chart_revenue_by_month', 'chart_revenue_by_model', 'chart_quote_funnel',
  'chart_revenue_by_customer', 'chart_revenue_by_pricing_group', 'chart_machines_sold',
]);

/**
 * The tile chrome: title, controls, and the body its widget renders into.
 * Every dashboard tile is a Lumina card floating on the gradient ground.
 */
export function WidgetFrame({
  instance, def, isEditing, onRemove, onResize, onToggleCollapse, onDragStart, onDragOver, onDrop,
  children, controls,
}: {
  instance: WidgetInstance;
  def?: WidgetDef;
  isEditing: boolean;
  onRemove: () => void;
  onResize: (size: WidgetSize) => void;
  onToggleCollapse: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  children: ReactNode;
  controls?: ReactNode;
}) {
  return (
    <section
      draggable={isEditing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={clsx(
        'card min-w-0 px-5 pb-5 pt-4',
        instance.size === 'sm' && 'md:col-span-3',
        instance.size === 'md' && 'md:col-span-6',
        instance.size === 'lg' && 'md:col-span-12',
        'col-span-12',
        isEditing && 'cursor-grab outline-dashed outline-2 outline-offset-2 outline-secondary/40 active:cursor-grabbing',
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-3 border-b border-divider pb-2.5">
        <button
          onClick={onToggleCollapse}
          className="label flex items-center gap-1.5 text-left hover:text-ink"
          aria-expanded={!instance.collapsed}
        >
          <span aria-hidden className="text-[9px] text-ink-tertiary">{instance.collapsed ? '▸' : '▾'}</span>
          {def?.label ?? instance.widget}
        </button>

        <div className="flex shrink-0 items-center gap-3">
          {!instance.collapsed && controls}
          {isEditing && (
            <>
              <div className="flex items-center gap-1">
                {(['sm', 'md', 'lg'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => onResize(s)}
                    className={clsx(
                      'label px-1',
                      instance.size === s ? 'text-ink underline decoration-secondary decoration-2 underline-offset-4' : 'text-ink-tertiary hover:text-ink',
                    )}
                    aria-label={`Size ${s}`}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>
              <button onClick={onRemove} aria-label="Remove widget" className="label text-negative hover:opacity-70">
                ×
              </button>
            </>
          )}
        </div>
      </div>

      {!instance.collapsed && children}
    </section>
  );
}

/** Renders whichever widget the instance names, fetching its own data. */
export function Widget({ widgetKey }: { widgetKey: string }) {
  const [range, setRange] = useState('12m');
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', widgetKey, RANGED.has(widgetKey) ? range : null],
    queryFn: () => api.get<any>(`/api/dashboard/widget/${widgetKey}`, RANGED.has(widgetKey) ? { range } : undefined),
    retry: false,
  });

  if (error) {
    return (
      <p className="py-4 font-sans text-caption text-negative">
        {(error as Error).message || 'This widget could not load.'}
      </p>
    );
  }
  if (isLoading) return <WidgetSkeleton widgetKey={widgetKey} />;
  if (!data) return <EmptyState message="No data." />;

  const rangeSelect = RANGED.has(widgetKey) ? (
    <Select
      value={range}
      onChange={(e) => setRange(e.target.value)}
      options={RANGES}
      size="sm"
      className="w-36"
      aria-label="Date range"
    />
  ) : null;

  if (widgetKey.startsWith('kpi_')) return <KpiWidget data={data} />;
  if (widgetKey.startsWith('chart_')) {
    return (
      <>
        {rangeSelect && <div className="mb-3 flex justify-end">{rangeSelect}</div>}
        <ChartWidget data={data} onDrill={(key) => {
          if (!data.drillTo) return;
          const url = data.drillParam ? `${data.drillTo}?${data.drillParam}=${key}` : data.drillTo;
          navigate(url);
        }} />
      </>
    );
  }
  if (widgetKey.startsWith('table_')) return <TableWidget widgetKey={widgetKey} data={data} />;
  if (widgetKey === 'feed_activity') return <ActivityWidget rows={data.rows} />;
  if (widgetKey === 'feed_notifications') return <NotificationsWidget rows={data.rows} />;

  return <EmptyState message="Unknown widget." />;
}

function WidgetSkeleton({ widgetKey }: { widgetKey: string }) {
  if (widgetKey.startsWith('kpi_')) {
    return (
      <div className="space-y-2">
        <div className="skeleton h-7 w-32" />
        <div className="skeleton h-3 w-24" />
      </div>
    );
  }
  if (widgetKey.startsWith('chart_')) return <div className="skeleton h-48 w-full" />;
  return <SkeletonRows rows={4} cols={3} />;
}

// ─── KPI ─────────────────────────────────────────────────────────────────────

function KpiWidget({ data }: { data: any }) {
  const value =
    data.value === null || data.value === undefined ? '—'
    : data.isPercent ? percent(data.value)
    : data.isCount ? number(data.value)
    : money(data.value);

  return (
    <div>
      <div className="flex items-baseline gap-3">
        <span className="metric">
          {value}
          {data.unit && value !== '—' && (
            <span className="ml-1.5 text-[15px] text-ink-tertiary">{data.unit}</span>
          )}
        </span>
        {data.changePercent !== undefined && data.changePercent !== null && (
          <Delta value={data.changePercent} />
        )}
      </div>
      {data.comparisonLabel && (
        <p className="mt-1 text-caption text-ink-tertiary">{data.comparisonLabel}</p>
      )}
    </div>
  );
}

// ─── Charts ──────────────────────────────────────────────────────────────────

function ChartWidget({ data, onDrill }: { data: any; onDrill: (key: string) => void }) {
  const series: Array<Record<string, any>> = data.series ?? [];
  if (series.length === 0) return <EmptyState message="Not enough data to plot yet." />;

  const isMoney = String(series[0]?.value ?? '').includes('.');
  const fmt = (v: unknown) => (isMoney ? money(v) : number(v));

  if (data.kind === 'donut') {
    const rows = series.map((s) => ({ ...s, value: Number(s.value) })).filter((s) => s.value > 0);
    if (rows.length === 0) return <EmptyState message="Nothing to show yet." />;
    return (
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows} dataKey="value" nameKey="label"
              innerRadius="55%" outerRadius="80%" paddingAngle={1}
              onClick={(e: any) => onDrill(e?.payload?.key)}
              cursor={data.drillTo ? 'pointer' : undefined}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={SERIES_COLOURS[i % SERIES_COLOURS.length]} stroke="rgba(0,0,0,0)" />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any) => number(v)} />
            <Legend wrapperStyle={{ fontFamily: fonts.sans, fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const stacks: string[] = data.stacks ?? [];
  const horizontal = data.layout === 'horizontal';
  const rows = series.map((s) => {
    const out: Record<string, any> = { ...s };
    if (stacks.length === 0) out.value = Number(s.value);
    else for (const k of stacks) out[k] = Number(s[k] ?? 0);
    return out;
  });

  return (
    <div style={{ height: horizontal ? Math.max(180, rows.length * 34 + 40) : 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 12, bottom: 0, left: horizontal ? 8 : 0 }}
          onClick={(e: any) => {
            const key = e?.activePayload?.[0]?.payload?.key;
            if (key) onDrill(key);
          }}
        >
          <CartesianGrid stroke={chartChrome.grid} strokeDasharray="0" vertical={horizontal} horizontal={!horizontal} />
          {horizontal ? (
            <>
              <XAxis type="number" tick={AXIS} axisLine={{ stroke: chartChrome.grid }} tickLine={false} />
              <YAxis type="category" dataKey="label" tick={AXIS} axisLine={false} tickLine={false} width={130} />
            </>
          ) : (
            <>
              <XAxis dataKey="label" tick={AXIS} axisLine={{ stroke: chartChrome.grid }} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={64} />
            </>
          )}
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
            formatter={(v: any, name: any) => [fmt(v), humanise(String(name))]}
          />
          {stacks.length > 1 && <Legend wrapperStyle={{ fontFamily: fonts.sans, fontSize: 10 }} />}

          {stacks.length === 0 ? (
            <Bar dataKey="value" fill={SERIES_COLOURS[0]} radius={[4, 4, 0, 0]} cursor={data.drillTo ? 'pointer' : undefined} />
          ) : (
            stacks.map((k, i) => (
              <Bar
                key={k} dataKey={k} name={humanise(k)}
                stackId={data.kind === 'stacked-bar' ? 'a' : undefined}
                fill={SERIES_COLOURS[i % SERIES_COLOURS.length]}
                cursor={data.drillTo ? 'pointer' : undefined}
              />
            ))
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Tables ──────────────────────────────────────────────────────────────────

function TableWidget({ widgetKey, data }: { widgetKey: string; data: any }) {
  const rows: any[] = data.rows ?? [];
  if (rows.length === 0) return <EmptyState message="Nothing to show." />;

  const cell = (children: ReactNode, numeric = false) => (
    <td className={clsx('py-2 pr-3', numeric && 'text-right font-sans tabular')}>{children}</td>
  );

  const head = (labels: Array<[string, boolean?]>) => (
    <tr className="rule">
      {labels.map(([l, n]) => (
        <th key={l} className={clsx('label pb-2 pr-3 text-left font-medium', n && 'text-right')}>{l}</th>
      ))}
    </tr>
  );

  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className="w-full border-collapse text-body">
        <thead>
          {widgetKey === 'table_recent_orders' && head([['Order'], ['Customer'], ['Status'], ['Total', true]])}
          {widgetKey === 'table_recent_quotes' && head([['Quote'], ['Customer'], ['Status'], ['Total', true]])}
          {widgetKey === 'table_overdue_invoices' && head([['Invoice'], ['Customer'], ['Due', true], ['Outstanding', true]])}
          {widgetKey === 'table_trials_expiring' && head([['Trial'], ['Customer'], ['Ends', true], ['Remaining', true]])}
          {widgetKey === 'table_inbound_shipments' && head([['PO'], ['Supplier'], ['Status'], ['Landing', true]])}
          {widgetKey === 'table_low_stock_parts' && head([['Part'], ['Number'], ['In stock', true], ['Reorder at', true]])}
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="rule-hairline">
              {widgetKey === 'table_recent_orders' && (
                <>
                  {cell(<Link to={`/sales/orders/${r.id}`} className="font-sans text-caption hover:underline">{r.reference}</Link>)}
                  {cell(r.customer?.companyName)}
                  {cell(<StatusBadge status={r.status} />)}
                  {cell(money(r.total), true)}
                </>
              )}
              {widgetKey === 'table_recent_quotes' && (
                <>
                  {cell(<Link to={`/sales/quotes/${r.id}`} className="font-sans text-caption hover:underline">{r.reference}</Link>)}
                  {cell(r.customer?.companyName)}
                  {cell(<StatusBadge status={r.status} />)}
                  {cell(money(r.total), true)}
                </>
              )}
              {widgetKey === 'table_overdue_invoices' && (
                <>
                  {cell(<span className="font-sans text-caption">{r.reference}</span>)}
                  {cell(r.customer?.companyName)}
                  {cell(date(r.dueDate), true)}
                  {cell(money(Number(r.total) - Number(r.amountPaid)), true)}
                </>
              )}
              {widgetKey === 'table_trials_expiring' && (
                <>
                  {cell(<Link to={`/trials/${r.id}`} className="font-sans text-caption hover:underline">{r.reference}</Link>)}
                  {cell(r.customer?.companyName)}
                  {cell(date(r.endDate), true)}
                  {cell(
                    <span className={r.daysRemaining < 0 ? 'text-negative' : r.daysRemaining <= 2 ? 'text-ink' : 'text-ink-secondary'}>
                      {r.daysRemaining === null ? '—' : r.daysRemaining < 0 ? `${Math.abs(r.daysRemaining)}d over` : `${r.daysRemaining}d`}
                    </span>, true,
                  )}
                </>
              )}
              {widgetKey === 'table_inbound_shipments' && (
                <>
                  {cell(<Link to={`/procurement/purchase-orders/${r.id}`} className="font-sans text-caption hover:underline">{r.reference}</Link>)}
                  {cell(r.supplier?.name)}
                  {cell(<StatusBadge status={r.status} />)}
                  {cell(date(r.expectedLandingDate), true)}
                </>
              )}
              {widgetKey === 'table_low_stock_parts' && (
                <>
                  {cell(<Link to={`/products/parts/${r.id}`} className="hover:underline">{r.name}</Link>)}
                  {cell(<span className="font-sans text-caption text-ink-tertiary">{r.partNumber}</span>)}
                  {cell(<span className={r.stockLevel === 0 ? 'text-negative' : 'text-ink'}>{number(r.stockLevel)}</span>, true)}
                  {cell(number(r.reorderThreshold), true)}
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Feeds ───────────────────────────────────────────────────────────────────

function ActivityWidget({ rows }: { rows: any[] }) {
  if (!rows || rows.length === 0) return <EmptyState message="No recent activity." />;
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className="w-full border-collapse font-sans text-caption">
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="rule-hairline align-top">
              <td className="w-32 whitespace-nowrap py-1.5 pr-3 text-ink-tertiary tabular">{dateTime(a.createdAt)}</td>
              <td className="w-36 py-1.5 pr-3 text-ink-secondary">{a.user?.name ?? a.userLabel ?? 'System'}</td>
              <td className="py-1.5 pr-3 text-ink">{humanise(a.action)} {a.entityType}</td>
              <td className="w-24 py-1.5 text-right text-ink-tertiary">{a.entityId?.slice(-8) ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationsWidget({ rows }: { rows: any[] }) {
  if (!rows || rows.length === 0) return <EmptyState message="No unread notifications." />;
  return (
    <div className="divide-y divide-rule/50">
      {rows.map((n) => {
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-body text-ink">{n.title}</span>
              <span className="shrink-0 font-sans text-caption text-ink-tertiary">{relative(n.createdAt)}</span>
            </div>
            {n.body && <p className="mt-0.5 text-caption text-ink-tertiary">{n.body}</p>}
          </>
        );
        return n.link
          ? <Link key={n.id} to={n.link} className="block py-2 hover:bg-rule/30">{body}</Link>
          : <div key={n.id} className="py-2">{body}</div>;
      })}
    </div>
  );
}

export { Spinner };
